import { useState, useRef, useEffect, memo } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps {
  src: string | undefined | null;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  aspectRatio?: string;
  fallback?: React.ReactNode;
  priority?: boolean;
  onLoad?: () => void;
  onError?: () => void;
  /** CSS object-position value for focal point support (e.g., "50% 30%") */
  objectPosition?: string;
  /** Additional inline styles for the image element (e.g., transform for zoom) */
  style?: React.CSSProperties;
}

/**
 * Optimized image component with:
 * - Blur-up placeholder effect
 * - Lazy loading via IntersectionObserver
 * - Explicit dimensions to prevent layout shift
 * - Graceful error handling with fallback
 */
export const OptimizedImage = memo(function OptimizedImage({
  src,
  alt,
  width,
  height,
  className,
  aspectRatio = 'auto',
  fallback,
  priority = false,
  onLoad,
  onError,
  objectPosition = 'center',
  style,
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Track if image was ever in view to prevent re-hiding on re-renders
  const wasInViewRef = useRef(priority);

  // Reset loaded state when src changes
  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
  }, [src]);

  useEffect(() => {
    // If already was in view, keep it that way
    if (wasInViewRef.current) {
      setIsInView(true);
      return;
    }
    
    if (priority || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            wasInViewRef.current = true;
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '100px', // Start loading 100px before entering viewport
        threshold: 0,
      }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  if (!src || hasError) {
    return (
      <div
        ref={containerRef}
        className={cn(
          'bg-muted flex items-center justify-center',
          className
        )}
        style={{
          width: width ? `${width}px` : undefined,
          height: height ? `${height}px` : undefined,
          aspectRatio,
        }}
      >
        {fallback || (
          <div className="text-muted-foreground text-xs">No image</div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn('relative overflow-hidden', className)}
      style={{
        width: width ? `${width}px` : undefined,
        height: height ? `${height}px` : undefined,
        aspectRatio,
      }}
    >
      {/* Blur placeholder - only animate when not loaded */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}

      {/* Actual image */}
      {isInView && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            'w-full h-full object-cover transition-opacity duration-300 pointer-events-none',
            isLoaded ? 'opacity-100' : 'opacity-0'
          )}
          style={{ objectPosition, WebkitTouchCallout: 'none', ...style }}
        />
      )}
    </div>
  );
});

export default OptimizedImage;
